import React from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Modal, {
	ModalBody,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';

const messages = defineMessages({
	heading: {
		id: 'malleable-ui-components.remix.heading',
		defaultMessage: 'Remix',
		description: 'Heading displayed at the top of the Remix overlay panel',
	},
	selectedContent: {
		id: 'malleable-ui-components.remix.selected-content',
		defaultMessage: 'Selected content',
		description: 'Label shown above the displayed selected text content in the Remix overlay',
	},
	noContent: {
		id: 'malleable-ui-components.remix.no-content',
		defaultMessage: 'No content selected',
		description: 'Fallback message displayed when no text content is currently selected',
	},
});

const styles = cssMap({
	textDisplay: {
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
});

interface RemixOverlayProps {
	selectedText: string;
	onClose: () => void;
}

/**
 * A centered modal overlay that displays the currently selected content.
 * Used by the Remix feature in both the floating toolbar and block menu.
 */
export function RemixOverlay({ selectedText, onClose }: RemixOverlayProps) {
	const { formatMessage } = useIntl();
	const displayText = selectedText || formatMessage(messages.noContent);

	return (
		<ModalTransition>
			<Modal onClose={onClose} width="small" testId="remix-overlay">
				<ModalHeader hasCloseButton>
					<ModalTitle>
						<FormattedMessage {...messages.heading} />
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<Stack space="space.100">
						<Text size="small" weight="semibold" color="color.text.subtlest">
							<FormattedMessage {...messages.selectedContent} />
						</Text>
						<Box
							xcss={styles.textDisplay}
							padding="space.150"
							backgroundColor="color.background.neutral"
						>
							{displayText}
						</Box>
					</Stack>
				</ModalBody>
			</Modal>
		</ModalTransition>
	);
}
